import {Pagination} from '@store/common/pagination.model';
import {Currency} from '@store/common/organisation.enums';
import {SuppliersService} from '@store/suppliers/services';
import {ClientsContract} from '@store/clients/contracts';
import {ManagePrice, Price} from '@store/common/price.model';
import {SelectItem} from '@components/inputs/select/Select.component';
import {ManageSchedule, Schedule, ScheduleFrequency} from '@store/common/schedule.model';
import {OrganisationLight} from '@store/common/organisation.model';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {ServiceKind, ServiceKindLight, UnitOfMeasure} from '@store/settings/serviceKinds';
import {PricePeriod} from '@store/common/price-period.model';
import {Indexation} from '@store/settings/indexation';
import {Waste} from '@store/settings/wasteFamilies';
import {Container} from '@store/settings/containers';
import dayjs from '@utils/dayjs.utils';
import {ExecutionDistributionRule, ManageExecutionDistributionRule} from '@store/operations/executions';
import I18n from '@utils/i18n.utils';
import {Colors} from '@constants/colors.constants';

export enum ServiceValidationStatusEnum {
  WAITING_FOR_SP = 'WAITING_FOR_SP',
  VALIDATED = 'VALIDATED'
}

export namespace ServiceValidationStatusEnum {
  export const selectItems = (): SelectItem[] => [
    {
      label: I18n.t('global.validationStatus.WAITING_FOR_SP'),
      value: ServiceValidationStatusEnum.WAITING_FOR_SP,
      backgroundColor: Colors.warning
    },
    {
      label: I18n.t('global.validationStatus.VALIDATED'),
      value: ServiceValidationStatusEnum.VALIDATED,
      backgroundColor: Colors.success
    },
  ];
}

export interface DatahubSpec {
  idDatahub?: string;
  additionalInformation?: string;
}

export interface CreateClientServiceDto {
  services: {
    validationStatus?: ServiceValidationStatusEnum;
    supplierUnit?: SelectItem;
    kind: SelectItem;
    sellIndexation?: SelectItem;
    buyIndexation?: SelectItem;
    clientWasteName: string;
    pctBuyDiscountRule: number;
    pctSellDiscountRule: number;
    unitCostInCents: number;
    unitPriceInCents: number;
    validFromDate: Date;
    validToDate?: Date;
    canBeRequested: boolean;
    interco: boolean;
    intersite: boolean;
    clientInvoicingRequired: boolean;
    supplierInvoicingRequired: boolean;
    datahubSpec?: {
      idDatahub: string;
      additionalInformation: string;
    };
    execUnitName?: string;
  }[];
  contractName?: string;
  datahubSpec?: {
    idDatahub: string;
    additionalInformation: string;
  };
  clientInvoiceAddress?: SelectItem;
  clientUnit?: SelectItem;
  waste?: SelectItem;
  container?: SelectItem;
  contract?: SelectItem;
}

export interface ManageClientServiceInvoicingSplit {
  id?: number;
  clientOrganisation?: SelectItem;
  pctSplit?: number;
}

export interface ManageClientService {
  isFromGenericUnit?: boolean;
  id?: number;
  supplierService?: SuppliersService;
  contract?: ClientsContract;
  clientWasteName?: string;
  sellPrices?: ManagePrice[];
  pctSellDiscountRule?: number;
  pctBuyDiscountRule?: number;
  validFromDate?: Date;
  validToDate?: Date;
  schedules?: ManageSchedule[];
  invoicingSplits?: ManageClientServiceInvoicingSplit[];
  schedulesToDelete?: number[];
  canBeRequested?: boolean;
  clientInvoicingRequired?: boolean;
  supplierInvoicingRequired?: boolean;
  interco?: boolean;
  intersite?: boolean;
  buyPriceId?: number;
  pricePeriods?: PricePeriod[];
  sellIndexationId?: number;
  buyIndexationId?: number;

  supplierUnit?: SelectItem;
  waste?: SelectItem;
  container?: SelectItem;
  kind?: SelectItem;
  sellIndexation?: SelectItem;
  buyIndexation?: SelectItem;

  distributionRules?: ManageExecutionDistributionRule[];
  datahubSpec?: DatahubSpec;
  validationStatus?: ServiceValidationStatusEnum;

  execUnitName?: string;

  // sellPricesToAdd	[...]
  // sellPricesToDelete	[...]
  // sellPricesToUpdate	[...]
  // schedulesToAdd	[...]
  // schedulesToDelete	[...]
  // schedulesToUpdate	[...]
}

export interface ClientsServiceInvoicingSplit {
  id: number;
  clientOrganisation: OrganisationLight;
  pct: number;
}

export namespace ClientsServiceInvoicingSplit {
  export const toManageClientServiceInvoicingSplit = (split: ClientsServiceInvoicingSplit): ManageClientServiceInvoicingSplit => ({
    id: split.id,
    clientOrganisation: split.clientOrganisation ? {
      label: split.clientOrganisation.name,
      value: split.clientOrganisation.id,
    } : undefined,
    pctSplit: split.pct * 100,
  });
}

export interface ClientsServiceDetails {
  id: number;
  supplierService: SuppliersService;
  contract: ClientsContract;
  clientWasteName: string;
  sellPrices: Price[];
  pctSellDiscount: number;
  pctBuyDiscount: number;
  validFromDate: Date;
  validToDate: Date;
  schedules: Schedule[];
  serviceSplits: ClientsServiceInvoicingSplit[];
  canBeRequested?: boolean;
  interco: boolean;
  intersite: boolean;
  clientInvoicingRequired: boolean;
  supplierInvoicingRequired: boolean;
  buyPrice?: Price;
  pricePeriods?: PricePeriod[];
  sellIndexation?: Indexation;
  buyIndexation?: Indexation;
  isFromGenericUnit?: boolean;
  distributionRules?: ExecutionDistributionRule[];
  datahubSpec: DatahubSpec;
  execUnitName?: string;
  kind: ServiceKindLight;
  container: Container;
  waste: Waste;
  validationStatus: ServiceValidationStatusEnum;
}

export namespace ClientsServiceDetails {
  export const toManageClientsService = (service: ClientsServiceDetails): ManageClientService => ({
    id: service.id,
    supplierService: service.supplierService,
    contract: service.contract,
    datahubSpec: service.datahubSpec,
    clientWasteName: service.clientWasteName,
    sellPrices: service.sellPrices?.map(Price.toManagePrice),
    pctSellDiscountRule: service.pctSellDiscount * 100,
    pctBuyDiscountRule: service.pctBuyDiscount * 100,
    validFromDate: service.validFromDate,
    validToDate: service.validToDate,
    schedules: service.schedules.map((sch) => {
      if (sch.frequency === ScheduleFrequency.MONTHLY) {
        sch.isDayOfMonth = sch.dayOfMonth != null;
      }
      const startFrom = dayjs().utc().set('hour', sch.startHour).set('minute', sch.startMinutes).toDate();
      const endFrom = dayjs().utc().set('hour', sch.endHour).set('minute', sch.endMinutes).toDate();

      return {...sch, startFrom: startFrom, endFrom: endFrom};
    }),
    invoicingSplits: service.serviceSplits?.map(ClientsServiceInvoicingSplit.toManageClientServiceInvoicingSplit),
    canBeRequested: service.canBeRequested,
    interco: service.interco,
    intersite: service.intersite,
    clientInvoicingRequired: service.clientInvoicingRequired,
    supplierInvoicingRequired: service.supplierInvoicingRequired,
    buyPriceId: service.buyPrice?.id,
    pricePeriods: service.pricePeriods,
    supplierUnit: {
      label: service.supplierService?.supplierUnitName || '-',
      value: service.supplierService?.id,
    },
    waste: {
      label: service.waste?.name,
      value: service.waste?.id,
    },
    container: {
      label: service.container?.name,
      value: service.container?.id,
    },
    kind: {
      label: service.kind?.kind,
      value: service.kind?.id,
    },
    sellIndexation: service.sellIndexation ? {
      label: service.sellIndexation.name,
      value: service.sellIndexation.id,
    } : undefined,

    buyIndexation: service.buyIndexation ? {
      label: service.buyIndexation.name,
      value: service.buyIndexation.id,
    } : undefined,
    isFromGenericUnit: service.isFromGenericUnit,
    distributionRules: service.distributionRules?.map((dr) => ({
      clientOrganisationUnitId: dr.clientOrganisationUnit.id,
      clientOrganisationUnit: {label: dr.clientOrganisationUnit.name, value: dr.clientOrganisationUnit.id},
      rateInPercent: dr.rateInPercent,
    })),
    execUnitName: service.execUnitName,
    validationStatus: service.validationStatus,
    buyIndexationId: service.buyIndexation?.id,
    sellIndexationId: service.sellIndexation?.id
  });
}

export interface ClientsServicesFilters {
  validationStatus?: ServiceValidationStatusEnum;
  filterExpiredContracts: boolean;
  filterExpiredServices: boolean;
  search: string;
  tableFilters: TableFilter[];
  excludeTableFilters: TableFilter[];
}

export namespace ClientsServicesFilters {
  export const initTableFilters = (clientId?: string, clientUnitId?: string, supplierId?: string) => TableFilterType.initTableFilters([
    TableFilterType.CLIENT_ORGANISATION,
    TableFilterType.CLIENT_ORGANISATION_UNIT,
    TableFilterType.SUPPLIER_ORGANISATION,
    TableFilterType.SUPPLIER_ORGANISATION_UNIT,
    TableFilterType.WASTE_FAMILY,
    TableFilterType.WASTE,
    TableFilterType.CONTAINER,
    TableFilterType.SERVICE_KIND,
    TableFilterType.SERVICE_KIND_TYPE,
  ]).map((f) => ({
    ...f,
    hide: (!!clientId && f.type === TableFilterType.CLIENT_ORGANISATION) || (!!clientUnitId && f.type === TableFilterType.CLIENT_ORGANISATION_UNIT) || (!!supplierId && f.type === TableFilterType.SUPPLIER_ORGANISATION),
    values: (f.type === TableFilterType.CLIENT_ORGANISATION && !!clientId) || (f.type === TableFilterType.CLIENT_ORGANISATION_UNIT && !!clientUnitId) || (!!supplierId && f.type === TableFilterType.SUPPLIER_ORGANISATION) ? [{
      label: '',
      value: clientId || clientUnitId || supplierId
    }] : [],
  }));

  export const initExcludeTableFilters = (clientId?: string, clientUnitId?: string, supplierId?: string) => TableFilterType.initTableFilters([
    TableFilterType.EXCLUDE_CLIENT_ORGANISATION,
    TableFilterType.EXCLUDE_CLIENT_ORGANISATION_UNIT,
    TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION,
    TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION_UNIT,
    TableFilterType.EXCLUDE_WASTE_FAMILY,
    TableFilterType.EXCLUDE_WASTE,
    TableFilterType.EXCLUDE_CONTAINER,
    TableFilterType.EXCLUDE_SERVICE_KIND,
    TableFilterType.EXCLUDE_SERVICE_KIND_TYPE,
  ]).map((f) => ({
    ...f,
    hide: (!!clientId && f.type === TableFilterType.EXCLUDE_CLIENT_ORGANISATION) || (!!clientUnitId && f.type === TableFilterType.EXCLUDE_CLIENT_ORGANISATION_UNIT) || (!!supplierId && f.type === TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION),
    values: (f.type === TableFilterType.EXCLUDE_CLIENT_ORGANISATION && !!clientId) || (f.type === TableFilterType.EXCLUDE_CLIENT_ORGANISATION_UNIT && !!clientUnitId) || (!!supplierId && f.type === TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION) ? [{
      label: '',
      value: clientId || clientUnitId || supplierId
    }] : [],
  }));
}

export interface ClientsServicesPagination extends Pagination {
  data: ClientsService[];
}

export interface ClientsServiceUI {
  id: number;
  selected: boolean;
}

export interface ClientsService {
  id: number;
  createdAt: Date;
  clientWasteName: string;
  schedulesCount: number;
  kindName: string;
  wasteName: string;
  containerName: string;
  supplierOrganisationName: string;
  supplierOrgUnitId: number;
  supplierOrganisationUnitName: string;
  clientOrganisationName: string;
  clientOrganisationUnitName: string;
  buyPricesCount: number;
  buyPriceIfOnlyOne: number;
  buyPriceCurrency: Currency;
  sellPricesCount: number;
  sellPriceIfOnlyOne: number;
  sellPriceCurrency: Currency;
  canBeRequested: boolean;
  buyPrice?: Price;
  buyPriceId?: number;
  executionCount?: number;
  execUnitName1?: UnitOfMeasure;
  execUnitName2?: UnitOfMeasure;
  expired: boolean;
  contractReference: string;
  contractName: string;
  container: Container;
  waste: Waste;
  kind: ServiceKind;
  datahubSpec: DatahubSpec;
  validFromDate: Date;
  startDate: Date;
  execUnitName?: string;
  validationStatus: ServiceValidationStatusEnum;
}
